/**
 * HM-485 cluster 18 Modulo 5 Cassa: pagina /cassa.
 *
 * Spec §9: pagamenti, chiusura conto, chiusura Z fiscale.
 *
 * UI minima funzionale:
 *   - 4 KPI grandi (totale, contanti, carte, extras live)
 *   - Status Z closure di oggi (badge)
 *   - Lista movimenti (payments captured del giorno)
 *   - Bottone "Chiudi Z" (admin only) con conferma
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  type CassaMovement,
  getCassaMovements,
  getCassaToday,
  postZClose,
} from '../../api/cassa.js';
import { useAuth } from '../../stores/auth.js';

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatEur(cents: number): string {
  return `€ ${(cents / 100).toFixed(2)}`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function CassaPage() {
  const userRole = useAuth((s) => s.user?.role);
  const isAdmin = userRole === 'admin';
  const [date, setDate] = useState<string>(todayIso());
  const [zError, setZError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const todayQuery = useQuery({
    queryKey: ['cassa', 'today', date],
    queryFn: () => getCassaToday(date),
    staleTime: 30_000,
  });

  const movementsQuery = useQuery({
    queryKey: ['cassa', 'movements', date],
    queryFn: () => getCassaMovements(date),
    staleTime: 30_000,
  });

  const zCloseMut = useMutation({
    mutationFn: postZClose,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cassa'] });
      setZError(null);
    },
    onError: (err: Error) => setZError(err.message ?? 'Errore chiusura Z'),
  });

  const totals = todayQuery.data?.totals;
  const movements: CassaMovement[] = movementsQuery.data?.movements ?? [];
  const hasZ = !!todayQuery.data?.zClosure;

  const kpis = useMemo(
    () => [
      { label: 'Incasso totale', value: formatEur(totals?.totalCents ?? 0), big: true },
      { label: 'Contanti', value: formatEur(totals?.cashCents ?? 0) },
      { label: 'Carte', value: formatEur(totals?.cardCents ?? 0) },
      { label: 'Extras attivi', value: formatEur(totals?.extrasLiveCents ?? 0) },
    ],
    [totals],
  );

  return (
    <div className="px-6 py-6" data-testid="cassa-page">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#3D2F24', margin: 0 }}>Cassa</h1>
          <p style={{ fontSize: 13, color: '#7A6957', margin: '4px 0 0 0' }}>
            Riepilogo giornaliero · {todayQuery.data?.paymentsCount ?? 0} movimenti
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 12, color: '#7A6957' }}>
            Data:{' '}
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="cassa-date-picker"
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid #D4C4B0',
                fontSize: 13,
              }}
            />
          </label>
        </div>
      </div>

      {/* KPI grandi */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginBottom: 24,
        }}
        data-testid="cassa-kpis"
      >
        {kpis.map((k) => (
          <div
            key={k.label}
            style={{
              padding: '20px 24px',
              background: '#FFF',
              border: '1px solid #E8DECF',
              borderRadius: 12,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: '#7A6957',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 600,
              }}
            >
              {k.label}
            </div>
            <div
              style={{
                fontSize: k.big ? 32 : 22,
                fontWeight: 700,
                color: '#3D2F24',
                marginTop: 8,
              }}
            >
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Z closure status + bottone */}
      <div
        style={{
          padding: 16,
          background: hasZ ? '#E4EDDE' : '#FFF3E6',
          border: `1px solid ${hasZ ? '#A6C490' : '#F5C7A7'}`,
          borderRadius: 10,
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        data-testid="cassa-z-status"
      >
        <div>
          <div style={{ fontWeight: 600, color: '#3D2F24' }}>
            {hasZ ? '✓ Chiusura Z eseguita oggi' : '⚠ Chiusura Z non ancora eseguita'}
          </div>
          {hasZ && todayQuery.data?.zClosure && (
            <div style={{ fontSize: 12, color: '#7A6957', marginTop: 4 }}>
              ID Z: <code>{todayQuery.data.zClosure.zClosureId}</code> ·{' '}
              {formatTime(todayQuery.data.zClosure.createdAt)}
            </div>
          )}
        </div>
        {!hasZ && isAdmin && date === todayIso() && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Confermi chiusura fiscale Z di oggi?')) {
                zCloseMut.mutate();
              }
            }}
            disabled={zCloseMut.isPending}
            data-testid="cassa-z-close-button"
            style={{
              padding: '10px 20px',
              background: '#E97B3D',
              color: '#FFF',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {zCloseMut.isPending ? 'Chiusura…' : 'Chiudi Z'}
          </button>
        )}
      </div>

      {zError && (
        <div
          data-testid="cassa-z-error"
          style={{
            color: '#A8453A',
            background: '#FFE5DC',
            padding: 10,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {zError}
        </div>
      )}

      {/* Movimenti */}
      <h2 style={{ fontSize: 18, fontWeight: 600, color: '#3D2F24', marginBottom: 12 }}>
        Movimenti del giorno
      </h2>
      <div
        style={{
          border: '1px solid #E8DECF',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#FFF',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead style={{ background: '#FAF7F2' }}>
            <tr>
              <th style={th}>Ora</th>
              <th style={th}>Camera</th>
              <th style={th}>Codice booking</th>
              <th style={th}>Metodo</th>
              <th style={{ ...th, textAlign: 'right' }}>Importo</th>
              <th style={th}>Stato</th>
            </tr>
          </thead>
          <tbody>
            {movementsQuery.isLoading && (
              <tr>
                <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#7A6957' }}>
                  Caricamento…
                </td>
              </tr>
            )}
            {!movementsQuery.isLoading && movements.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  style={{ padding: 24, textAlign: 'center', color: '#7A6957' }}
                  data-testid="cassa-movements-empty"
                >
                  Nessun movimento per la data selezionata.
                </td>
              </tr>
            )}
            {movements.map((m) => (
              <tr
                key={m.id}
                style={{ borderTop: '1px solid #F0E8DA' }}
                data-testid={`cassa-mov-${m.id}`}
              >
                <td style={td}>{formatTime(m.createdAt)}</td>
                <td style={td}>{m.roomCode ?? '—'}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>
                  {m.publicCode ?? '—'}
                </td>
                <td style={td}>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: m.method === 'cash' ? '#E4EDDE' : '#DCE7F0',
                      color: m.method === 'cash' ? '#4A6B3A' : '#3A5B7A',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {m.method === 'cash' ? 'CONTANTI' : 'CARTA'}
                  </span>
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                  € {Number.parseFloat(m.amount).toFixed(2)}
                </td>
                <td style={td}>{m.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: '#7A6957',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const td: React.CSSProperties = {
  padding: '10px 14px',
  verticalAlign: 'middle',
};
